'use client';

import { ChevronRight, RotateCcw } from 'lucide-react';
import { ChatActionButton } from './chat-action-button';

type TheoryFooterProps = {
	type: 'theory';
	isRead: boolean;
	onNext: () => void;
};

type QuizFooterProps = {
	type: 'quiz';
	onNext: () => void;
};

type EditFooterProps = {
	type: 'edit';
	onReset: () => void;
	onSubmit: () => void;
};

type CompletedFooterProps = {
	type: 'completed';
	onNext: () => void;
	nextLabel: string;
	/** Зелёная (уход из интервью на nextHref) при true; иначе серая «Далее». */
	primary?: boolean;
};

export type SidePanelFooterProps = (
	| TheoryFooterProps
	| QuizFooterProps
	| EditFooterProps
	| CompletedFooterProps
) & {
	/** Действия доступны только когда диалог в idle (бэк готов принять). */
	disabled?: boolean;
};

const DISABLED_CLS = 'cursor-not-allowed opacity-40 pointer-events-none';

export function SidePanelFooter(props: SidePanelFooterProps) {
	const disabled = props.disabled ?? false;
	const disabledCls = disabled ? ` ${DISABLED_CLS}` : '';

	if (props.type === 'completed') {
		// Завершённое интервью: primary — дубль зелёной кнопки CompletedBanner
		// (уход на nextHref); иначе серая «Далее» к следующему файлу. Обе —
		// клиентская навигация, доступны независимо от статуса диалога.
		return (
			<div className="flex shrink-0 justify-end overflow-hidden border-t border-gray-4 px-4 py-3.5">
				<ChatActionButton
					onClick={props.onNext}
					variant={props.primary ? 'primary' : 'secondary'}
				>
					{props.nextLabel} <ChevronRight size={16} strokeWidth={2.5} />
				</ChatActionButton>
			</div>
		);
	}

	if (props.type === 'theory') {
		// Кнопка всегда «Далее»: открывает следующий файл из списка вложений.
		// Когда теория — часть активной статической задачи (isRead=false), клик
		// дополнительно подтверждает прочтение (серверное действие) и потому
		// требует idle-статуса; в режиме навигации (isRead) доступна всегда.
		const confirmDisabled = disabled && !props.isRead;
		return (
			<div className="flex shrink-0 justify-end overflow-hidden border-t border-gray-4 px-4 py-3.5">
				<ChatActionButton
					disabled={confirmDisabled}
					onClick={props.onNext}
					variant={props.isRead ? 'secondary' : 'primary'}
					title="Перейти к следующему файлу"
				>
					Далее
					<ChevronRight size={16} strokeWidth={2.5} />
				</ChatActionButton>
			</div>
		);
	}

	if (props.type === 'quiz') {
		// «Далее» — клиентская навигация, всегда доступна.
		return (
			<div className="flex shrink-0 justify-end overflow-hidden border-t border-gray-4 px-4 py-3.5">
				<ChatActionButton onClick={props.onNext} variant="secondary">
					Далее <ChevronRight size={16} strokeWidth={2.5} />
				</ChatActionButton>
			</div>
		);
	}

	return (
		<div className="flex shrink-0 items-center justify-between overflow-hidden border-t border-gray-4 px-4 py-3.5">
			<button
				type="button"
				disabled={disabled}
				onClick={props.onReset}
				title="Вернуть исходный шаблон"
				className={`flex size-9 cursor-pointer items-center justify-center rounded-lg border border-gray-4 text-gray-3 transition-colors hover:bg-gray-5 hover:text-black${disabledCls}`}
			>
				<RotateCcw size={16} strokeWidth={2} />
			</button>
			<ChatActionButton disabled={disabled} onClick={props.onSubmit}>
				Отправить <ChevronRight size={16} strokeWidth={2.5} />
			</ChatActionButton>
		</div>
	);
}
